import React, { useState, useContext, useEffect } from "react";
import { useSelector } from "react-redux";
import { Modal, Form, Button, Alert, Accordion } from "react-bootstrap";
import Select from "react-select";
import NewNumUsersDisplay from "../../../../components/wifi/NewNumUsersDisplay";
import useAuth from "../../../../hooks/useAuth";
import usePermissions from "../../../../hooks/usePermissions";
import axios from "axios";
import { apiConfig } from "../../../../config";
import NotyfContext from "../../../../contexts/NotyfContext";
import { ReactComponent as LogoLoader } from "../../../../assets/img/wavenet-animated-loader3.svg";
import MultipleUserCsvInput from "../MultipleUserCsvInput";
import { createRateLimitedAxios } from "utils/ratelimit";

const NewAddMultipleNew8021XCredentialModal = ({
  openModals,
  toggle,
  customerOptions,
  dispatch,
  setRefreshData,
  loading,
}) => {
  const notyf = useContext(NotyfContext);
  const selectedLandlord = useSelector((state) => state.landlordswitcher);
  const { userAccessToken } = useAuth();
  const bearerToken = `Bearer ${userAccessToken}`;

  const [excellid, setExcellId] = useState(null);
  const [SSID, setSSID] = useState("");
  const [validated, setValidated] = useState(false);

  const [responseErrors, setResponseErrors] = useState(null);

  const [numUsersIsLoading, setNumUsersIsLoading] = useState(true);
  const [numAvailable, setNumAvailable] = useState(0);
  const [numInUse, setNumInUse] = useState(0);

  const [availableSsids, setAvailableSsids] = useState([]);

  const rateLimitingAxios = createRateLimitedAxios();

  const [users, setUsers] = useState([]);
  const [invalidUsers, setInvalidUsers] = useState([]);
  const [progress, setProgress] = useState(0);

  const canUseCustomVlan = usePermissions("edit:custom_vlan:all");

  React.useEffect(() => {
    axios
      .get(apiConfig.credentialsApiUrl + "ssids/enabled", {
        headers: { Authorization: bearerToken },
      })
      .then((response) => {
        const filteredSSIDs = response.data.ssids.rows
          .filter(
            (ssid) =>
              ssid.building_operator_id ===
                parseInt(selectedLandlord.landlordid) &&
              ssid.credential_type === "dot1x"
          )
          .map((ssid) => ({ value: ssid.ssid, label: ssid.ssid }));
        setAvailableSsids(filteredSSIDs);
      });
  }, [selectedLandlord.landlordid, bearerToken]);

  const resetFormFields = () => {
    setExcellId(null);
    setSSID(null);
    setValidated(false);
    setUsers([]);
    setProgress(0);
    setResponseErrors(null);
  };

  const handleOnRowsChange = (validRows, invalidRows) => {
    setUsers(validRows);
    setInvalidUsers(invalidRows);
  };

  // Handle when users is changed, such as on a row change
  useEffect(() => {
    if (
      invalidUsers?.length > 0 ||
      users?.length === 0 ||
      users?.length > numAvailable
    ) {
      setValidated(false);
    } else {
      setValidated(true);
    }
  }, [users, invalidUsers, numAvailable]);

  const handleSubmit = async (event) => {
    event.preventDefault();

    let isErrorState = false;

    let receivedErrors = [];
    setProgress((progress) => 0);
    for (const user of users) {
      const createCredentialBody = {
        username: user.email,
        firstname: user.firstname,
        lastname: user.lastname,
        email: user.email,
        excellid,
        SSID,
        wavenetIdList: [excellid],
        userlevel: user.userlevel,
        custom_vlan: user.customvlan,
        building_operator_id: selectedLandlord.landlordid,
        sendInviteEmail: true,
      };

      try {
        const response = await rateLimitingAxios({
          method: "POST",
          url: apiConfig.credentialsApiUrl + "users/",
          data: createCredentialBody,
          headers: { Authorization: bearerToken },
        });
        if (response.data.success !== 1) {
          isErrorState = true;
          receivedErrors.push({
            username: user.email,
            error: response?.data?.error || "An unexpected error occurred",
          });
        }
      } catch (error) {
        isErrorState = true;
        receivedErrors.push({
          username: user.email,
          error: error?.response?.data?.error || "An error occurred.",
        });
      }
      setProgress((progress) => progress + 1);
    }
    if (isErrorState) {
      notyf.open({
        type: "warning",
        message: `An error occurred. ${receivedErrors.length}/${users.length} users failed to submit.`,
        duration: 3000,
        ripple: true,
        dismissible: true,
        position: { x: "right", y: "top" },
      });
      setResponseErrors(receivedErrors);
      console.error({
        message: "Error submitting multiple users",
        errors: receivedErrors,
      });
    } else {
      notyf.open({
        type: "success",
        message: `Successfully created ${users.length} users`,
        duration: 3000,
        ripple: true,
        dismissible: true,
        position: { x: "right", y: "top" },
      });
      toggle("addMultipleNewCredential");
      resetFormFields();
      setTimeout(() => dispatch(setRefreshData()), 2000);
    }
  };
  return (
    <Modal
      show={openModals["addMultipleNewCredential"]}
      onHide={() => {
        toggle("addMultipleNewCredential");
        resetFormFields();
      }}
      centered
    >
      <Form noValidate validated={validated} onSubmit={handleSubmit}>
        <Modal.Header closeButton>
          <b>Admin Function:</b> Add Multiple New 802.1X Credentials
        </Modal.Header>
        <Modal.Body className="m-3">
          <Form.Group className="mb-3">
            <Form.Label>
              Company Name
              {loading && (
                <LogoLoader
                  className="d-inline-block align-middle ms-2"
                  style={{ width: "20px", height: "20px" }}
                />
              )}
            </Form.Label>
            <Select
              className="react-select-container"
              classNamePrefix="react-select"
              options={customerOptions}
              placeholder="Select..."
              onChange={(e) => setExcellId(e.value)}
              value={
                excellid
                  ? customerOptions.find((o) => o.value === excellid)
                  : null
              }
            />
          </Form.Group>

          {excellid && (
            <NewNumUsersDisplay
              excellid={excellid}
              isLoading={numUsersIsLoading}
              setIsLoading={setNumUsersIsLoading}
              numAvailable={numAvailable}
              setNumAvailable={setNumAvailable}
              numInUse={numInUse}
              setNumInUse={setNumInUse}
            />
          )}

          {excellid && !numUsersIsLoading && numAvailable > 0 && (
            <>
              <hr />
              <Form.Group className="mb-3">
                <Form.Label>SSID</Form.Label>
                <Select
                  className="react-select-container"
                  classNamePrefix="react-select"
                  options={availableSsids}
                  onChange={(e) => setSSID(e.value)}
                  required
                />
                <Form.Control.Feedback type="invalid">
                  Please select an SSID.
                </Form.Control.Feedback>
              </Form.Group>

              {excellid && SSID && !numUsersIsLoading && numAvailable > 0 && (
                <MultipleUserCsvInput
                  onRowsChange={handleOnRowsChange}
                  maxUsers={numAvailable}
                  canUseCustomVlan={canUseCustomVlan[1] === true}
                />
              )}
            </>
          )}

          {excellid && !numUsersIsLoading && numAvailable <= 0 && (
            <Alert className="my-3 p-2">
              This account does not have any available wi-fi users.{" "}
              <a href="#" onClick={() => toggle("wifiPurchase")}>
                Request more.
              </a>
            </Alert>
          )}

          {progress !== 0 && progress !== users.length && (
            <Alert className="my-3 p-2" variant="info">
              Please wait... Uploading users {progress}/{users.length}
            </Alert>
          )}

          {responseErrors && (
            <Accordion className="my-3 p-2 text-lg">
              <Accordion.Item eventKey="0">
                <Accordion.Header>Response Errors</Accordion.Header>
                <Accordion.Body>
                  <code>
                    <pre>{JSON.stringify(responseErrors, null, 2)}</pre>
                  </code>
                </Accordion.Body>
              </Accordion.Item>
            </Accordion>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => toggle("addMultipleNewCredential")}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            className=""
            variant={!validated ? "secondary" : "info"}
            disabled={!validated}
          >
            Create Credentials
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
};

export default NewAddMultipleNew8021XCredentialModal;
